import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, CalendarHeartIcon, FacebookIcon, UsersIcon } from 'lucide-react';
import { Seo } from '../components/ui/Seo';
import { PageHero } from '../components/ui/PageHero';
import { ButtonLink } from '../components/ui/ButtonLink';
import { SectionHeading } from '../components/ui/SectionHeading';
import { ImageSlot } from '../components/ui/ImageSlot';
import { StarDivider } from '../components/ui/StarDivider';
import { InquiryForm } from '../components/forms/InquiryForm';
import { IMAGES } from '../data/images';
import { BOOKING_URL, SITE } from '../data/site';
const womenCourses = [{
  code: 'W-CCW',
  name: "Women's Multi-State CCW",
  to: '/courses/womens-ccw',
  note: '8 hours · Permit course'
}, {
  code: 'W-ITDH',
  name: "Women's Defensive Handgun",
  to: '/courses/womens-itdh',
  note: '4 hours · Beginner'
}, {
  code: 'FOHD',
  name: 'Fundamentals of Home Defense',
  to: '/courses/fohd',
  note: 'Women-only sessions monthly'
}, {
  code: 'FOCC',
  name: 'Fundamentals of Concealed Carry',
  to: '/courses/focc',
  note: 'Carry options for women'
}, {
  code: 'RENEW',
  name: 'NV CCW Renewal',
  to: '/courses/nv-ccw-renewal',
  note: 'Women-only dates available'
}, {
  code: '1-ON-1',
  name: 'Private Lessons with a Female Instructor',
  to: '/private-training',
  note: 'Scheduled around you'
}];
export function WomensTraining() {
  return <>
      <Seo title="Women’s Firearms Training Las Vegas" description="Women-only CCW and defensive handgun classes taught by women since 2013. Women’s firearms training in Las Vegas and the Women’s Defense Network." data-id="element-955" data-mp="2a9t7b9fwe30v" />
      <PageHero title="Train in an environment where you feel most comfortable." sub="Women-only classes, taught by women, since 2013." image={IMAGES.women} crumbs={[{
      label: "Women's Training"
    }]} data-id="element-956" data-mp="2k9k1tb4gki0y">
        <ButtonLink href={BOOKING_URL} size="lg" data-id="element-957" data-mp="3vpqstlam8zk8">
          Book a Women-Only Class
        </ButtonLink>
        <ButtonLink href="#group-class" size="lg" variant="outline-light" data-id="element-958" data-mp="3qr797rjhvzv1">
          Build a Private Group Class
        </ButtonLink>
      </PageHero>

      <section className="gutter section grid items-center gap-14 bg-white lg:grid-cols-12" aria-labelledby="story-title" data-id="element-959" data-mp="6kezdrno950i">
        <ImageSlot image={IMAGES.maggie} className="aspect-[4/5] rounded-[4px] lg:col-span-5" data-id="element-960" data-mp="blm5lma61w52" />
        <div className="lg:col-span-7" data-id="element-961" data-mp="gj4ifnrb0vic">
          <SectionHeading id="story-title" title="Why Maggie started women-only classes" data-id="element-962" data-mp="3f1tcs2rsibil" />
          <div className="t-lead mt-6 max-w-[48rem] space-y-5 text-steel" data-id="element-963" data-mp="3k0mg52dd4m51">
            <p data-id="element-964" data-mp="3jyjqijg4gv0u">
              In 2013, Maggie noticed something in her mixed classes: many women held back their questions. Some had been “taught” by a spouse or friend and left more nervous than when they started.
            </p>
            <p data-id="element-965" data-mp="2wbbj9vz4vbw6">
              So she built a different room. Women-only classes, taught by women instructors, where every question is welcome and you learn at your pace. The result? Students who leave genuinely confident — and keep coming back.
            </p>
          </div>
          <figure className="mt-8 border-l-4 border-accent pl-6" data-id="element-966" data-mp="2to9ki8tyxcve">
            <blockquote className="font-display text-2xl font-semibold uppercase leading-snug text-navy" data-id="element-967" data-mp="2os2bg769zl98">“Self-Defense isn’t just a right, it is also a Responsibility.”</blockquote>
            <figcaption className="mt-2 text-sm text-steel" data-id="element-968" data-mp="2esbgk4dsz48m">— CCW Maggie</figcaption>
          </figure>
        </div>
      </section>

      <section className="gutter section bg-mist" aria-labelledby="w-courses-title" data-id="element-969" data-mp="2at2or52bpgfp">
        <SectionHeading id="w-courses-title" title="Courses offered in a women-only format" data-id="element-970" data-mp="25zgko1a86kvi" />
        <ul className="mt-10 grid border-t border-navy/15 md:grid-cols-2 2xl:grid-cols-3" data-id="element-971" data-mp="212q65ie2jxc7">
          {womenCourses.map(c => <li key={c.name} className="border-b border-navy/15 md:odd:pr-8 md:even:pl-8 2xl:px-6" data-id="element-972" data-mp="1w0r3kaq5l4gb">
              <Link to={c.to} className="group flex items-center gap-5 py-6" data-id="element-973" data-mp="1r5lgh8l0ljqn">
                <span className="w-20 shrink-0 font-display text-sm font-semibold tracking-[0.12em] text-accent" data-id="element-974" data-mp="1n2yvnrmrgz2y">{c.code}</span>
                <span className="flex-1" data-id="element-975" data-mp="2grwlllpicqel">
                  <span className="block font-display text-xl font-semibold uppercase text-navy group-hover:underline group-hover:decoration-accent group-hover:underline-offset-4" data-id="element-976" data-mp="2gquyujz4qfh1">{c.name}</span>
                  <span className="text-sm text-steel" data-id="element-977" data-mp="2711m3fn93ub7">{c.note}</span>
                </span>
                <ArrowRightIcon className="h-5 w-5 text-navy transition-transform duration-150 group-hover:translate-x-1" aria-hidden="true" data-id="element-978" data-mp="1t228r456v1uc" />
              </Link>
            </li>)}
        </ul>
      </section>

      <section className="gutter section bg-midnight" aria-labelledby="wdn-title" data-id="element-979" data-mp="1k2yovxy76ies">
        <div className="grid gap-12 lg:grid-cols-12" data-id="element-980" data-mp="1o2h185bydl60">
          <div className="lg:col-span-6" data-id="element-981" data-mp="1o521wr0ilv26">
            <StarDivider align="left" data-id="element-982" data-mp="3efepoj8854c9" />
            <h2 id="wdn-title" className="t-h2 mt-6 text-white" data-id="element-983" data-mp="3egg7d6jqwp2y">Women’s Defense Network</h2>
            <span className="red-rule mt-5" aria-hidden="true" data-id="element-984" data-mp="3iggtldd364kk" />
            <p className="t-lead mt-6 max-w-[42rem] text-chrome-100" data-id="element-985" data-mp="16t4uy0t7fodc">
              A community of women who train together. Monthly clinics keep your skills sharp, and you’ll meet women at every level — from first-time shooters to experienced carriers.
            </p>
            <div className="mt-8" data-id="element-986" data-mp="1bszm85gw6fyx">
              <ButtonLink href={SITE.social.womensDefenseNetwork} size="lg" newTab data-id="element-987" data-mp="1bryarzvyrrgo">
                <FacebookIcon className="h-4 w-4" aria-hidden="true" data-id="element-988" data-mp="1qh4arq2mllpm" /> Join on Facebook
              </ButtonLink>
            </div>
          </div>
          <ul className="grid gap-x-10 sm:grid-cols-2 lg:col-span-6" data-id="element-989" data-mp="1lij53b4lu9ts">
            {[{
            Icon: CalendarHeartIcon,
            title: 'Monthly clinics',
            body: 'Focused skill sessions on a new topic every month.'
          }, {
            Icon: UsersIcon,
            title: 'A supportive community',
            body: 'Train with women who understand your goals.'
          }].map(({
            Icon,
            title,
            body
          }) => <li key={title} className="border-t border-white/15 py-7" data-id="element-990" data-mp="711opaigx5x">
                <Icon className="h-7 w-7 text-chrome-300" aria-hidden="true" data-id="element-991" data-mp="208r3vp1ifrok" />
                <h3 className="t-h3 mt-4 text-white" data-id="element-992" data-mp="256agylmh1yex">{title}</h3>
                <p className="mt-2 text-chrome-300" data-id="element-993" data-mp="258wq2pp0glz9">{body}</p>
              </li>)}
          </ul>
        </div>
      </section>

      <section id="group-class" className="gutter section scroll-mt-24 grid gap-12 bg-mist lg:grid-cols-12" aria-labelledby="group-title" data-id="element-994" data-mp="2e8095to6h1dr">
        <div className="lg:col-span-4" data-id="element-995" data-mp="2j621743l0b1d">
          <SectionHeading id="group-title" title="Build a private class for your group" intro="Friends, family, a book club or your workplace — we’ll design a women-only class around your schedule." data-id="element-996" data-mp="2o5oefdjetra6" />
        </div>
        <div className="lg:col-span-8" data-id="element-997" data-mp="2xvzkb5mnmajp">
          <InquiryForm title="Tell us about your group" topicLabel="Course of interest" topics={womenCourses.map(c => c.name)} showGroupSize submitLabel="Request My Group Class" data-id="element-998" data-mp="2xuy9915n1o1z" />
        </div>
      </section>
    </>;
}